// Domain model mirroring the Go backend's JSON API (see backend/internal/agenda/model.go).

export type MeetingType = string;

export type ItemType = string;

export type SpacingProfile = 'compact' | 'medium' | 'loose';

export interface Author {
  name: string;
  pronouns?: string;
}

export interface TimeRange {
  start?: string;
  end?: string;
  duration?: number;
  independent?: boolean;
}

export interface RecurrenceOverride {
  date: string; // ISO 8601 date string
  applied: boolean;
  cancelled?: boolean;
}

export interface Recurrence {
  scheme?: string; // e.g., "weekly", "monthly", "manual", "biweekly", "daily", "yearly"
  pattern?: string; // recurrence pattern (e.g., RRULE-like format)
  startDate?: string; // ISO 8601 date string
  endDate?: string; // ISO 8601 date string
  overrides?: RecurrenceOverride[];
}

export interface Subitem {
  emoji?: string;
  title: string;
  leader?: string;
  body?: string;
  subitems?: Subitem[];
}

/** A file in the content-addressable public store, attached to an item. */
export interface Attachment {
  hash: string;
  filename: string;
  contentType: string;
  size?: number;
  /** Optional display label; defaults to filename. */
  title?: string;
}

export interface Item {
  id: string;
  updatedAt?: string;
  type: ItemType;
  timeRange?: TimeRange;
  emoji?: string;
  title: string;
  leader?: string;
  /** HTML fragment: plain text or text with <b>, <i>, <a href="">. */
  body?: string;
  recurring?: boolean;
  recurrence?: Recurrence;
  appliesThisMeeting?: boolean;
  subitems?: Subitem[];
  attachments?: Attachment[];
}

export interface Agenda {
  id: string;
  version: number;
  meetingType: MeetingType;
  organization?: string;
  series?: string;
  title: string;
  date: string;
  meetingTime?: string;
  location?: string;
  author?: Author;
  items: Item[];
  totalMinutes: number;
  spacingProfile?: SpacingProfile;
}

export interface AgendaSummary {
  id: string;
  meetingType: MeetingType;
  title: string;
  date: string;
  location?: string;
  organization?: string;
  totalMinutes: number;
}

export interface Permissions {
  readTypes: MeetingType[];
  writeTypes: MeetingType[];
}

export interface AgendaListResponse {
  agendas: AgendaSummary[];
  permissions: Permissions;
}

// ─── Timeline ──────────────────────────────────────────────────────────────

export interface TimingIssue {
  itemIndex: number;
  itemTitle: string;
  message: string;
}

export interface TimelineResult {
  agenda: Agenda;
  issues?: TimingIssue[];
}

// ─── Publish ───────────────────────────────────────────────────────────────

export type PublishTarget = 'markdown' | 'html' | 'pdf' | 'pdf-13x19' | 'ghost';

export interface PublishResult {
  status: string;
  outputs: Record<string, string>;
  errors: Record<string, string>;
}

// ─── Tenant ────────────────────────────────────────────────────────────────

export interface Theme {
  stylesheet: string;
  accents: Record<string, string>;
  /** Sidebanner graphic for PDF output (server-side only; not rendered in the web UI). */
  sideBanner?: string;
}

export interface Cadence {
  weekday: number;
  defaultTime: string;
}

export interface ItemTypeConfig {
  key: string;
  label: string;
}

export interface MeetingTypeConfig {
  key: string;
  label: string;
  accent: string;
  cadence: Cadence;
  titleFormat: string;
  defaults: {
    organization: string;
    series: string;
    location: string;
  };
  itemTypes: ItemTypeConfig[];
  defaultItemType: string;
}

export interface Tenant {
  id: string;
  displayName: string;
  theme: Theme;
  meetingTypes: MeetingTypeConfig[];
}

// ─── Display helpers ─────────────────────────────────────────────────────────

/**
 * Get the label for an item type from tenant config.
 * Fallback to a titleized version of the key if not found in config.
 */
export function getItemTypeLabel(
  itemType: ItemType,
  meetingTypeConfig?: MeetingTypeConfig,
): string {
  if (meetingTypeConfig) {
    const found = meetingTypeConfig.itemTypes.find((it) => it.key === itemType);
    if (found) return found.label;
  }
  // Fallback: titleize the key
  return itemType
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get valid item types for a meeting type from tenant config.
 */
export function getValidItemTypes(meetingTypeConfig: MeetingTypeConfig): ItemType[] {
  return meetingTypeConfig.itemTypes.map((it) => it.key as ItemType);
}

/**
 * Get the default item type for a meeting type from tenant config.
 */
export function getDefaultItemType(meetingTypeConfig: MeetingTypeConfig): ItemType {
  return meetingTypeConfig.defaultItemType as ItemType;
}

/**
 * Get the accent color for a meeting type from tenant theme.
 */
export function getAccentColor(
  meetingTypeKey: string,
  tenant?: Tenant | null,
): string {
  if (!tenant) {
    // Fallback CSS variables for default tenant (for backward compatibility)
    return meetingTypeKey === 'general'
      ? 'var(--accent-general)'
      : 'var(--accent-steering)';
  }

  const meetingType = tenant.meetingTypes.find((mt) => mt.key === meetingTypeKey);
  if (meetingType && tenant.theme.accents[meetingType.accent]) {
    return tenant.theme.accents[meetingType.accent];
  }

  // Fallback
  return 'var(--accent)';
}
